import React from 'react';
import { Typography } from '@mui/material';
import LegalPageLayout from './LegalPageLayout';

const BusinessCustomerAgreement = () => {
  return (
    <LegalPageLayout
      title="Business Customer Agreement"
      updated="October 13, 2026"
    >
      <Typography paragraph>
        This Business Customer Agreement applies to organizations
        using HaggleFreight for business shipment and transportation
        needs. It supplements the Customer Agreement and Terms of
        Service when presented and accepted by an authorized
        representative.
      </Typography>

      <Typography paragraph>
        HaggleFreight is a transportation marketplace operated by
        GoCourier Inc. This agreement concerns use of the Platform;
        it does not appoint GoCourier Inc. to enter transportation
        contracts on the organization's behalf.
      </Typography>

      <Typography variant="h5" gutterBottom>
        1. Business Account
      </Typography>

      <Typography paragraph>
        The accepting representative must have authority to bind
        the identified organization. Provide accurate legal or
        business name, contact information, account details, and
        relevant billing information.
      </Typography>

      <Typography paragraph>
        Disclose material changes affecting account authority or
        lawful use. Do not represent that an unrelated entity's
        licenses, insurance, credit, or business identity applies
        to the account.
      </Typography>

      <Typography variant="h5" gutterBottom>
        2. Authorized Users
      </Typography>

      <Typography paragraph>
        The organization is responsible for activity it authorizes
        employees, contractors, and other representatives to
        perform. Ensure that users understand applicable
        agreements and operate within their authority.
      </Typography>

      <Typography paragraph>
        Protect credentials, maintain appropriate access
        restrictions, and revoke access when authorization ends.
        Contact support when assistance is needed to restrict
        former personnel.
      </Typography>

      <Typography paragraph>
        These obligations do not transfer responsibility for
        GoCourier Inc.'s own security failures or authorize
        charges that were never properly agreed.
      </Typography>

      <Typography variant="h5" gutterBottom>
        3. Shipments and Freight
      </Typography>

      <Typography paragraph>
        Provide accurate cargo, timing, equipment, location,
        contact, and special-handling information before
        confirming transportation.
      </Typography>

      <Typography paragraph>
        Identify refrigeration, unusual dimensions, loading and
        unloading responsibilities, dock requirements, security
        procedures, appointments, and other material conditions.
        Ensure relevant personnel have the instructions needed
        for lawful and safe performance.
      </Typography>

      <Typography paragraph>
        Platform records do not replace required bills of lading,
        permits, shipping documents, or regulatory disclosures.
        Responsibilities remain allocated by law and accepted
        agreements.
      </Typography>

      <Typography variant="h5" gutterBottom>
        4. Rates and Negotiation
      </Typography>

      <Typography paragraph>
        Only authorized personnel should confirm shipments,
        negotiate changes, or approve additional services.
        The organization must honor commitments made within
        their authority.
      </Typography>

      <Typography paragraph>
        An estimate, unaccepted quote, or automated acknowledgment
        is not itself a confirmed transportation agreement.
        Additional charges require an applicable accepted term
        or other lawful basis.
      </Typography>

      <Typography paragraph>
        Purchase orders, facility documents, and internal policies
        do not impose new obligations on a provider or
        GoCourier Inc. unless appropriately presented and agreed
        or otherwise required by law.
      </Typography>

      <Typography variant="h5" gutterBottom>
        5. Subscriptions and Business Plans
      </Typography>

      <Typography paragraph>
        No future subscription price, guaranteed free-access
        period, credit limit, discount, or business feature is
        promised by this agreement. Optional features apply
        only when actually offered.
      </Typography>

      <Typography paragraph>
        A paid plan applies after a specific offer is presented
        and accepted. Price, billing, renewal, cancellation,
        included features, and refund conditions must be
        disclosed before purchase.
      </Typography>

      <Typography paragraph>
        Creating an account or accepting this agreement does not
        authorize a new charge, automatic conversion to paid
        access, or recurring billing at an undisclosed price.
        Transportation charges remain separate.
      </Typography>

      <Typography variant="h5" gutterBottom>
        6. Compliance
      </Typography>

      <Typography paragraph>
        Maintain the authority necessary to offer the cargo and
        act for the relevant owner or organization. Any regulated
        intermediary activity requires its own applicable
        qualifications and authority.
      </Typography>

      <Typography paragraph>
        The business is responsible for lawful cargo and the
        packaging, labeling, documentation, and other duties
        imposed on it by law and the accepted agreement.
        A Platform account is not a license or regulatory
        clearance.
      </Typography>

      <Typography variant="h5" gutterBottom>
        7. Prohibited and Restricted Cargo
      </Typography>

      <Typography paragraph>
        Follow the Acceptable Use Policy and applicable legal
        restrictions. Disclosure alone does not make an
        unsupported or prohibited category permissible.
      </Typography>

      <Typography paragraph>
        Do not conceal hazardous characteristics, misdescribe
        cargo, or pressure a provider to perform work outside
        its qualifications. A refusal or removed listing does
        not automatically determine entitlement to a refund
        or cancellation charge.
      </Typography>

      <Typography variant="h5" gutterBottom>
        8. Billing and Payment
      </Typography>

      <Typography paragraph>
        Provide accurate billing information and ensure that
        personnel approving payments have appropriate authority.
        Review the payee, amount, payment conditions, and any
        additional charges before authorization.
      </Typography>

      <Typography paragraph>
        This agreement is not blanket authorization for unspecified
        penalties, recurring charges, payment adjustments, or
        deductions. Amounts must have an applicable contractual
        or legal basis and required authorization.
      </Typography>

      <Typography paragraph>
        Report billing errors promptly. A good-faith payment
        dispute does not itself constitute abuse. Knowingly
        false claims, stolen credentials, and attempts at
        duplicate recovery may be investigated.
      </Typography>

      <Typography paragraph>
        Each party retains the tax, invoicing, reporting, and
        other financial obligations imposed on it by law.
      </Typography>

      <Typography variant="h5" gutterBottom>
        9. Independent Transportation Providers
      </Typography>

      <Typography paragraph>
        Physical transportation is performed by the relevant
        Transportation Provider rather than GoCourier Inc.
        Review the provider and its suitability for the
        particular shipment.
      </Typography>

      <Typography paragraph>
        Profiles, reviews, and verification indicators are not
        universal guarantees of current authority, coverage,
        performance, or availability. These limitations do not
        excuse an inaccurate description of checks performed
        by HaggleFreight or remove our own legal responsibilities.
      </Typography>

      <Typography variant="h5" gutterBottom>
        10. Facility Access and Personal Information
      </Typography>

      <Typography paragraph>
        Provide relevant appointments, contacts, access
        instructions, parking restrictions, security procedures,
        and equipment requirements. Do not require unsafe or
        unlawful conduct at a facility.
      </Typography>

      <Typography paragraph>
        Provide information about staff, drivers, and recipients
        only with an appropriate lawful basis and required
        notices or permissions. Limit personnel access to
        information needed for their work.
      </Typography>

      <Typography paragraph>
        The Privacy Policy governs GoCourier Inc.'s processing.
        The business remains responsible for its separate
        processing and records. Contract acceptance does not
        waive individuals' nonwaivable privacy rights.
      </Typography>

      <Typography variant="h5" gutterBottom>
        11. Disputes and Records
      </Typography>

      <Typography paragraph>
        Preserve relevant agreements, shipment documents,
        messages, invoices, and other evidence. Provide truthful
        information during a dispute.
      </Typography>

      <Typography paragraph>
        HaggleFreight may assist with records and actions within
        its responsibility. Support review is not binding
        arbitration or a guarantee that another party will
        pay a claim.
      </Typography>

      <Typography paragraph>
        Do not allow a carrier, insurer, bank, or legal deadline
        to expire while waiting for support. The Refund Policy
        and Cancellation Policy govern their respective subjects.
      </Typography>

      <Typography variant="h5" gutterBottom>
        12. Insurance and Risk
      </Typography>

      <Typography paragraph>
        Evaluate appropriate cargo coverage and relevant limits
        before booking. GoCourier Inc. does not itself provide
        cargo insurance.
      </Typography>

      <Typography paragraph>
        A declared value, uploaded certificate, or Platform
        transaction does not automatically establish coverage
        for a loss. Applicable responsibility remains governed
        by law and valid agreements.
      </Typography>

      <Typography variant="h5" gutterBottom>
        13. Restrictions, Closure, and Deletion
      </Typography>

      <Typography paragraph>
        Access may be restricted for legitimate, proportionate
        reasons under the Terms. A good-faith complaint,
        chargeback, safety report, or privacy request is not
        itself grounds for punishment.
      </Typography>

      <Typography paragraph>
        Account deletion and outstanding obligations are handled
        under the Terms and Privacy Policy. Closure does not
        authorize confiscation of funds or indefinite retention
        of the entire account.
      </Typography>

      <Typography variant="h5" gutterBottom>
        14. Contact
      </Typography>

      <Typography paragraph>
        Contact support@hagglefreight.com for account or policy
        assistance. The Terms govern changes, document priority,
        applicable liability and indemnification provisions,
        and disputes.
      </Typography>

      <Typography paragraph>
        This agreement does not create a personal payment guarantee
        merely because an authorized representative accepts for
        a properly identified organization. It does not excuse
        fraud, misrepresentation of authority, or another
        independent legal responsibility.
      </Typography>
    </LegalPageLayout>
  );
};

export default BusinessCustomerAgreement;